/* ============================================================
   PHREE SKIN — /science signature graphic: the LIVING MICROBIOME.
   A canvas "petri field" on a light ground: multi-species cells drift,
   wobble, divide (mitosis) and link to their nearest neighbours with
   faint filaments — the Braille dot grammar, alive. Three key actives
   are anchored with leader lines into the colony; a pH scale marks the
   skin's healthy acid window (4.5–5.5).

   Cool-biased palette so it belongs to the brand: sky / sky-deep /
   charcoal, with two disciplined biology outliers (a reactive coral, a
   diversity sage) that live ONLY inside the canvas — never in the UI.

   Discipline: single rAF, DPR-aware, IntersectionObserver-gated,
   fully static + calm under prefers-reduced-motion. No deps.
   ============================================================ */
(function () {
  "use strict";

  /* legacy petri field — superseded by /common/microbiome.js (the REACTIVE
     engine now owns #mbCanvas). This only runs if a page still ships the old
     #mbCanvasLegacy element; on /science it no-ops. */
  var canvas = document.getElementById("mbCanvasLegacy");
  if (!canvas) return;
  var stage = canvas.closest(".mbstage") || canvas.parentElement;
  var ctx = canvas.getContext("2d");
  var DPR = Math.min(2, window.devicePixelRatio || 1);
  var reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---- species (each a real organism register, cool-biased) ---- */
  var SPECIES = [
    { key: "sky",     rgb: [115, 179, 206] },   /* commensal — the good crowd */
    { key: "deep",    rgb: [ 78, 150, 180] },   /* probiotic Lactobacillus     */
    { key: "charcoal",rgb: [ 31,  58,  80] },   /* structural / anchor microbe */
    { key: "sage",    rgb: [143, 184, 168] },   /* diversity                   */
    { key: "coral",   rgb: [225, 167, 155] }    /* the reactive one, kept few  */
  ];
  /* weighted draw — a HEALTHY microbiome is mostly the good crowd + diversity,
     only a little coral. */
  var POOL = [0,0,0,1,1,0,3,3,2,0,1,4,0,3,1,0];

  var W = 0, H = 0, cells = [], filaments = [], raf = 0, running = false, t0 = 0;
  var mouse = { x: -9999, y: -9999, on: false };

  function rnd(a, b) { return a + Math.random() * (b - a); }

  function makeCell(x, y, r, sp, born) {
    return {
      x: x, y: y, r: r, sp: sp,
      vx: rnd(-0.14, 0.14), vy: rnd(-0.11, 0.11),
      phase: rnd(0, 6.28), wob: rnd(0.4, 1.1),      /* membrane wobble       */
      spin: rnd(-0.0006, 0.0006), rot: rnd(0, 6.28),
      grain: (Math.random() * 0.5 + 0.5),           /* inner nucleolus tint  */
      born: born || 0,                              /* for the divide pop-in */
      splitAt: rnd(7000, 15000) + (born || 0),      /* next mitosis time     */
      cool: rnd(0.9, 1.1)
    };
  }

  function seed() {
    cells = [];
    var area = W * H;
    var target = Math.max(14, Math.min(30, Math.round(area / 26000)));
    var tries = 0;
    while (cells.length < target && tries < target * 40) {
      tries++;
      var r = rnd(9, 22);
      var x = rnd(r + 6, W - r - 6), y = rnd(r + 6, H - r - 6);
      var ok = true;
      for (var i = 0; i < cells.length; i++) {
        var dx = cells[i].x - x, dy = cells[i].y - y;
        if (dx * dx + dy * dy < (cells[i].r + r + 10) * (cells[i].r + r + 10)) { ok = false; break; }
      }
      if (!ok) continue;
      cells.push(makeCell(x, y, r, POOL[(Math.random() * POOL.length) | 0], 0));
    }
  }

  function resize() {
    var rect = stage.getBoundingClientRect();
    W = Math.max(1, rect.width); H = Math.max(1, rect.height);
    canvas.width = Math.round(W * DPR); canvas.height = Math.round(H * DPR);
    canvas.style.width = W + "px"; canvas.style.height = H + "px";
    seed();
  }

  /* ---- one cell: soft membrane, cool rim-light, faint nucleus ---- */
  function drawCell(c, tt, pop) {
    var col = SPECIES[c.sp].rgb;
    var wob = reduce ? 0 : Math.sin(tt / 900 + c.phase) * c.wob;
    var rr = (c.r + wob) * pop;
    ctx.save();
    ctx.translate(c.x, c.y);
    ctx.rotate(c.rot);

    /* body — radial fill, brighter core, translucent so overlaps read as depth */
    var g = ctx.createRadialGradient(-rr * 0.28, -rr * 0.30, rr * 0.15, 0, 0, rr);
    g.addColorStop(0, "rgba(" + col[0] + "," + col[1] + "," + col[2] + ",0.92)");
    g.addColorStop(0.7, "rgba(" + col[0] + "," + col[1] + "," + col[2] + ",0.70)");
    g.addColorStop(1, "rgba(" + col[0] + "," + col[1] + "," + col[2] + ",0.30)");
    ctx.beginPath();
    /* gently lobed membrane (not a perfect circle) */
    for (var a = 0; a <= 6.2832; a += 0.35) {
      var lob = 1 + (reduce ? 0 : Math.sin(a * 3 + c.phase + tt / 1100) * 0.05);
      var px = Math.cos(a) * rr * lob, py = Math.sin(a) * rr * lob;
      a === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fillStyle = g; ctx.fill();

    /* cool rim light — the "wet" premium sheen */
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(255,255,255,0.45)";
    ctx.stroke();

    /* nucleus */
    ctx.beginPath();
    ctx.arc(-rr * 0.16, -rr * 0.10, rr * 0.30 * c.grain, 0, 6.2832);
    ctx.fillStyle = "rgba(" + col[0] + "," + col[1] + "," + col[2] + ",0.55)";
    ctx.fill();
    /* specular dot */
    ctx.beginPath();
    ctx.arc(-rr * 0.34, -rr * 0.36, rr * 0.12, 0, 6.2832);
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.fill();
    ctx.restore();
  }

  function step(now) {
    if (!t0) t0 = now;
    var tt = now - t0;
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.clearRect(0, 0, W, H);

    var i, j, c, d;

    /* filaments — connect the nearest few (the dot-grid, made living tissue) */
    for (i = 0; i < cells.length; i++) {
      c = cells[i];
      for (j = i + 1; j < cells.length; j++) {
        d = cells[j];
        var dx = d.x - c.x, dy = d.y - c.y, dist = Math.sqrt(dx * dx + dy * dy);
        var reach = c.r + d.r + 62;
        if (dist < reach) {
          var a = 0.16 * (1 - dist / reach);
          ctx.strokeStyle = "rgba(78,150,180," + a.toFixed(3) + ")";
          ctx.lineWidth = 1;
          ctx.beginPath(); ctx.moveTo(c.x, c.y); ctx.lineTo(d.x, d.y); ctx.stroke();
        }
      }
    }

    /* cells — motion, gentle repulsion, walls, cursor push, mitosis */
    for (i = 0; i < cells.length; i++) {
      c = cells[i];
      if (!reduce) {
        c.x += c.vx * c.cool; c.y += c.vy * c.cool;
        c.rot += c.spin;
        /* soft separation so they crowd but don't stack */
        for (j = 0; j < cells.length; j++) {
          if (j === i) continue;
          d = cells[j];
          var ox = c.x - d.x, oy = c.y - d.y, od = Math.sqrt(ox * ox + oy * oy) || 1;
          var min = c.r + d.r + 4;
          if (od < min) {
            var push = (min - od) / min * 0.04;
            c.x += (ox / od) * push; c.y += (oy / od) * push;
          }
        }
        /* cursor gently parts the colony */
        if (mouse.on) {
          var mx = c.x - mouse.x, my = c.y - mouse.y, mdd = Math.sqrt(mx * mx + my * my);
          if (mdd < 120 && mdd > 0.5) {
            var mp = (1 - mdd / 120) * 0.9;
            c.x += (mx / mdd) * mp; c.y += (my / mdd) * mp;
          }
        }
        /* bounce off walls */
        if (c.x < c.r + 4) { c.x = c.r + 4; c.vx = Math.abs(c.vx); }
        if (c.x > W - c.r - 4) { c.x = W - c.r - 4; c.vx = -Math.abs(c.vx); }
        if (c.y < c.r + 4) { c.y = c.r + 4; c.vy = Math.abs(c.vy); }
        if (c.y > H - c.r - 4) { c.y = H - c.r - 4; c.vy = -Math.abs(c.vy); }
      }

      /* divide pop-in easing for freshly-born cells */
      var age = tt - c.born;
      var pop = age < 700 ? (0.5 + 0.5 * ease(Math.min(1, age / 700))) : 1;
      drawCell(c, tt, pop);

      /* mitosis — a cell splits into two when its clock elapses (cap density) */
      if (!reduce && tt > c.splitAt && cells.length < 34 && c.r > 11) {
        c.r *= 0.74;
        c.splitAt = tt + rnd(9000, 17000);
        var nx = c.x + rnd(-c.r, c.r), ny = c.y + rnd(-c.r, c.r);
        var baby = makeCell(
          Math.max(c.r + 4, Math.min(W - c.r - 4, nx)),
          Math.max(c.r + 4, Math.min(H - c.r - 4, ny)),
          c.r, c.sp, tt
        );
        cells.push(baby);
      }
    }

    /* label leader-lines: draw a thin sky filament from each active tag to the
       nearest cell of its species, so the labels feel wired into the biology */
    drawLeaders();

    if (running && !reduce) raf = requestAnimationFrame(step);
  }

  function ease(x) { return 1 - Math.pow(1 - x, 3); }

  /* leader anchors are DOM tags with data-mb-anchor="species:xNorm,yNorm" */
  var anchors = [];
  function collectAnchors() {
    anchors = [];
    stage.querySelectorAll("[data-mb-anchor]").forEach(function (el) {
      var v = el.getAttribute("data-mb-anchor").split(":");
      var xy = v[1].split(",");
      anchors.push({ el: el, spKey: v[0], nx: +xy[0], ny: +xy[1] });
    });
  }
  function speciesIndex(k) { for (var i = 0; i < SPECIES.length; i++) if (SPECIES[i].key === k) return i; return 0; }

  function drawLeaders() {
    if (!anchors.length) return;
    var rect = stage.getBoundingClientRect();
    anchors.forEach(function (an) {
      var b = an.el.getBoundingClientRect();
      /* skip tags hidden by responsive rules (display:none → zero-size rect):
         no visible label means no leader line. */
      if (b.width < 1 || b.height < 1) return;
      /* start point = the tag's inner edge, mapped into canvas space */
      var sx = (b.left + b.width / 2) - rect.left;
      var sy = (b.top + b.height / 2) - rect.top;
      /* target = nearest cell of this species to the tag's declared normal point */
      var tx = an.nx * W, ty = an.ny * H;
      var spi = speciesIndex(an.spKey), best = null, bestD = 1e9;
      for (var i = 0; i < cells.length; i++) {
        if (cells[i].sp !== spi) continue;
        var dx = cells[i].x - tx, dy = cells[i].y - ty, dd = dx * dx + dy * dy;
        if (dd < bestD) { bestD = dd; best = cells[i]; }
      }
      if (!best) return;
      var col = SPECIES[spi].rgb;
      ctx.save();
      ctx.setLineDash([1, 4]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(" + col[0] + "," + col[1] + "," + col[2] + ",0.55)";
      ctx.beginPath(); ctx.moveTo(sx, sy);
      var mx = (sx + best.x) / 2, my = (sy + best.y) / 2 - 14;
      ctx.quadraticCurveTo(mx, my, best.x, best.y);
      ctx.stroke();
      ctx.setLineDash([]);
      /* ring the anchored cell */
      ctx.beginPath(); ctx.arc(best.x, best.y, best.r + 5, 0, 6.2832);
      ctx.strokeStyle = "rgba(" + col[0] + "," + col[1] + "," + col[2] + ",0.5)";
      ctx.stroke();
      ctx.restore();
    });
  }

  function start() { if (!running) { running = true; t0 = 0; raf = requestAnimationFrame(step); } }
  function stop() { running = false; cancelAnimationFrame(raf); }

  /* ---- interaction + lifecycle ---- */
  stage.addEventListener("mousemove", function (e) {
    var r = stage.getBoundingClientRect(); mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; mouse.on = true;
  });
  stage.addEventListener("mouseleave", function () { mouse.on = false; mouse.x = mouse.y = -9999; });

  var deb;
  window.addEventListener("resize", function () {
    clearTimeout(deb);
    deb = setTimeout(function () { resize(); collectAnchors(); if (reduce) renderStatic(); }, 180);
  });

  function renderStatic() {
    /* one calm, composed frame for reduced-motion */
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.clearRect(0, 0, W, H);
    var i, j;
    for (i = 0; i < cells.length; i++) for (j = i + 1; j < cells.length; j++) {
      var a = cells[i], b = cells[j], dx = b.x - a.x, dy = b.y - a.y, dist = Math.sqrt(dx * dx + dy * dy), reach = a.r + b.r + 62;
      if (dist < reach) { ctx.strokeStyle = "rgba(78,150,180," + (0.16 * (1 - dist / reach)).toFixed(3) + ")"; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); }
    }
    for (i = 0; i < cells.length; i++) drawCell(cells[i], 0, 1);
    drawLeaders();
  }

  resize();
  collectAnchors();

  var io = new IntersectionObserver(function (es) {
    es.forEach(function (en) {
      if (en.isIntersecting) { reduce ? renderStatic() : start(); }
      else stop();
    });
  }, { threshold: 0 });
  io.observe(canvas);

  /* labels/pH can fade in with the shared reveal system; re-anchor after fonts settle */
  window.addEventListener("load", function () { collectAnchors(); if (reduce) renderStatic(); });
})();


/* ============================================================
   BOX GRAPHICS (Microbiome 101): the 1,000-cell species grid + the pH bar.
   The clock is inline SVG in the page. Cool-biased, glass-chip aesthetic.
   ============================================================ */
(function boxViz() {
  "use strict";
  var DPR = Math.min(2, window.devicePixelRatio || 1);

  function rr(c, x, y, w, h, r) {
    c.beginPath(); c.moveTo(x + r, y);
    c.arcTo(x + w, y, x + w, y + h, r); c.arcTo(x + w, y + h, x, y + h, r);
    c.arcTo(x, y + h, x, y, r); c.arcTo(x, y, x + w, y, r); c.closePath();
  }

  /* 1,000 species = a 40×25 mosaic of cool-varied chips (a few warm/sage outliers) */
  document.querySelectorAll("[data-viz-grid]").forEach(function (cv) {
    var ctx = cv.getContext("2d"), drawn = false;
    function draw() {
      var b = cv.getBoundingClientRect(), W = b.width, H = b.height;
      if (W < 4 || H < 4) return;
      cv.width = Math.round(W * DPR); cv.height = Math.round(H * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0); ctx.clearRect(0, 0, W, H);
      var cols = 40, rows = 25, gap = 1.1;                     /* 1,000 dots — a Braille field */
      var cw = (W - (cols - 1) * gap) / cols, ch = (H - (rows - 1) * gap) / rows;
      var r = Math.min(cw, ch) * 0.46;
      for (var y = 0; y < rows; y++) for (var x = 0; x < cols; x++) {
        var hue = 196 + (Math.random() * 42 - 16), sat = 30 + Math.random() * 44, lig = 56 + Math.random() * 28;
        var o = Math.random();
        if (o < 0.02) { hue = 14 + Math.random() * 14; sat = 46; lig = 73; }        /* rare reactive coral */
        else if (o < 0.06) { hue = 150 + Math.random() * 18; sat = 24; lig = 71; }   /* diversity sage      */
        ctx.fillStyle = "hsl(" + hue + "," + sat + "%," + lig + "%)";
        ctx.beginPath(); ctx.arc(x * (cw + gap) + cw / 2, y * (ch + gap) + ch / 2, r, 0, 6.28319); ctx.fill();
      }
      drawn = true;
    }
    new IntersectionObserver(function (es) { es.forEach(function (e) { if (e.isIntersecting && !drawn) draw(); }); }, { threshold: 0.15 }).observe(cv);
    var rt; window.addEventListener("resize", function () { clearTimeout(rt); rt = setTimeout(draw, 200); }, { passive: true });
    requestAnimationFrame(function () { if (cv.getBoundingClientRect().top < innerHeight * 1.2) draw(); });
  });

  /* pH scale — cells 2.5 → 8.5 in half-steps; the 4.5–5.5 acid-mantle window glows */
  document.querySelectorAll("[data-viz-ph]").forEach(function (el) {
    var html = "";
    for (var p = 2.5; p <= 8.5 + 1e-9; p += 0.5) {
      var zone = p >= 4.5 && p <= 5.5;
      html += '<span class="phcell' + (zone ? " is-zone" : "") + '"></span>';
    }
    el.innerHTML = '<div class="phbar">' + html + "</div>";
  });
})();
